import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import { ARTICLES, articleHref } from "@/data/blogArticles";
import { FadeUp, Eyebrow } from "@/lib/motion";

export function BucketGrid({ articles = ARTICLES }) {
  return (
    <div className="grid gap-6 md:grid-cols-3">
      {articles.map((a, i) => (
        <FadeUp key={a.slug} delay={i * 0.08}>
          <Link
            to={articleHref(a)}
            data-testid={`blog-bucket-${a.slug}`}
            className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-flame/40 hover:shadow-xl"
          >
            <div className="border-b border-slate-100 bg-coal/50 p-5">
              <img
                src={a.graphic}
                alt={`${a.category} illustration`}
                className="w-full rounded-xl"
              />
            </div>
            <div className="flex flex-1 flex-col p-7">
              <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-flame">
                {a.category}
              </div>
              <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                {a.title}
              </h3>
              <div className="mt-auto pt-6">
                <span className="flex items-center gap-1.5 font-mono text-[13px] uppercase tracking-[0.15em] text-flame">
                  Read the guide
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
                <span className="mt-3 flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.15em] text-faint">
                  <Clock className="h-3.5 w-3.5" />
                  {a.readTime}
                </span>
              </div>
            </div>
          </Link>
        </FadeUp>
      ))}
    </div>
  );
}

export default function BlogSection() {
  return (
    <section
      id="blog"
      data-testid="blog-buckets-section"
      className="relative bg-ink py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <FadeUp>
          <Eyebrow>Resources &amp; guides</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
            Make marketing work{" "}
            <span className="text-flame">on your website.</span>
          </h2>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-mist md:text-xl">
            Practical resources and plain-English guides for business owners —
            no jargon, no hype, just what actually moves leads and calls.
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <Link
            to={articleHref(
              ARTICLES.find(
                (a) => a.slug === "kansas-city-geo-for-home-service-businesses"
              )
            )}
            data-testid="home-new-post-teaser"
            className="group mt-10 flex flex-col gap-3 rounded-2xl border border-flame/30 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/60 hover:shadow-lg sm:flex-row sm:items-center sm:gap-5 sm:p-6"
          >
            <span className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-flame/10 px-4 py-1.5 font-mono text-[12px] font-semibold uppercase tracking-[0.15em] text-flame sm:self-center">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-flame" />
              New on the blog
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-display text-lg font-bold tracking-tight text-slate-900">
                Kansas City GEO for Home Service Businesses: 3 Case Studies
              </span>
              <span className="mt-0.5 block text-[15px] leading-snug text-faint">
                Plumbers, HVAC and roofers getting named in AI answers — most
                inside 2-3 months.
              </span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-flame transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
        <div className="mt-14">
          <BucketGrid articles={ARTICLES.slice(0, 3)} />
        </div>
        <FadeUp delay={0.25}>
          <div className="mt-10 flex justify-center">
            <Link
              to="/blog"
              data-testid="blog-section-all-link"
              className="group flex items-center gap-2 rounded-2xl border border-slate-300 px-6 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-all duration-300 hover:border-flame/60 hover:text-flame"
            >
              Visit the blog
              <ArrowUpRight className="h-4 w-4 text-flame transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
